import React, { forwardRef, useRef, useEffect } from 'react';
import Type from 'prop-types';
import { capitalize, noop } from 'lodash';
import {
  Divider,
  InputBase,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Clear as ClearIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import cn from 'classnames';
import { useTranslation } from 'hooks';
import styles from './SearchField.module.css';

const SearchField = forwardRef(function SearchField(props, ref) {
  const {
    loading,
    value,
    position,
    className,
    visible,
    fullWidth,
    showDivider,
    disableGutters,
    action,
    Component,
    backIcon = <ArrowBackIcon />,
    clearIcon = <ClearIcon />,
    startAdornment: startAdornmentProp,
    endAdornment: endAdornmentProp,
    inputProps,
    backButtonProps,
    clearButtonProps,
    onChange,
    onBackClick,
    onClearClick,
    onSubmit,
    inputRef: inputRefProp,
    ...other
  } = props;

  const defaultInputRef = useRef();
  const inputRef = inputRefProp ?? defaultInputRef;

  const isControlled = value !== undefined;
  const positionClassName = `position${capitalize(position)}`;
  const { t } = useTranslation();

  useEffect(() => {
    if (inputRef && inputRef.current) {
      if (visible) inputRef.current.focus();
      if (!visible) inputRef.current.blur();
    }
  }, [inputRef, visible]);

  const handleChange = (event) => {
    const newValue = event.target.value;

    if (onChange) {
      onChange(event, newValue, 'input');
    }
  };

  const defaultStartAdornment = (
    <InputAdornment position="start">
      <IconButton
        edge="start"
        onClick={onBackClick}
        data-testid="closeSearchBtn"
        {...backButtonProps}
      >
        {backIcon}
      </IconButton>
    </InputAdornment>
  );

  const defaultEndAdornment = (
    <InputAdornment position="end">
      <IconButton edge="end" onClick={onClearClick} {...clearButtonProps}>
        {clearIcon}
      </IconButton>
    </InputAdornment>
  );

  const startAdornment =
    startAdornmentProp !== undefined ? startAdornmentProp : defaultStartAdornment;
  const endAdornment = endAdornmentProp !== undefined ? endAdornmentProp : defaultEndAdornment;

  return (
    <>
      <Component
        action={action}
        ref={ref}
        className={cn(className, {
          [styles.root]: true,
          [styles[positionClassName]]: true,
          [styles.fullWidth]: fullWidth,
          [styles.gutters]: !disableGutters,
        })}
        role="search"
        style={{
          visibility: visible ? 'visible' : 'hidden',
        }}
        onSubmit={onSubmit}
      >
        <InputBase
          value={value}
          onChange={isControlled ? handleChange : onChange}
          startAdornment={startAdornment}

          endAdornment={
            loading ? (
              <CircularProgress color="inherit" size={24} classes={{ root: styles.loader }} />
            ) : (
              endAdornment
            )
          }
          fullWidth={fullWidth}
          autoComplete="off"
          inputProps={{
            role: 'combobox',
            'aria-label': t('search'),
            'aria-haspopup': 'false',
            "type": "text",
            ...inputProps,
          }}
          inputRef={inputRef}
          {...other}
        />
      </Component>
      {showDivider && <Divider />}
    </>
  );
});

SearchField.propTypes = {
  value: Type.oneOfType([Type.string, Type.number]),
  position: Type.oneOf(['static', 'absolute']),
  visible: Type.bool,
  fullWidth: Type.bool,
  showDivider: Type.bool,
  disableGutters: Type.bool,
  loading: Type.bool,
  className: Type.string,
  /**
   * [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Attributes) applied to the `input` element.
   */
  inputProps: Type.object, // eslint-disable-line react/forbid-prop-types
  // eslint-disable-next-line react/forbid-prop-types
  inputRef: Type.oneOfType([Type.func, Type.shape({ current: Type.any })]),
  Component: Type.node,
  backIcon: Type.node,
  clearIcon: Type.node,
  backButtonProps: Type.object, // eslint-disable-line react/forbid-prop-types
  clearButtonProps: Type.object, // eslint-disable-line react/forbid-prop-types
  action: Type.string,
  startAdornment: Type.node,
  endAdornment: Type.node,
  onChange: Type.func,
  onBackClick: Type.func,
  onClearClick: Type.func,
  onSubmit: Type.func,
};

SearchField.defaultProps = {
  value: undefined,
  position: 'absolute',
  visible: true,
  fullWidth: false,
  showDivider: false,
  disableGutters: false,
  loading: false,
  className: undefined,
  inputProps: {},
  action: undefined,
  inputRef: undefined,
  backButtonProps: {},
  clearButtonProps: {},
  Component: 'div',
  backIcon: undefined,
  clearIcon: undefined,
  startAdornment: undefined,
  endAdornment: undefined,
  onChange: noop,
  onBackClick: noop,
  onClearClick: noop,
  onSubmit: noop,
};

export default SearchField;
